// drawOla cloud sync engine. Fork-only.
//
// Background sync for draws that carry a cloud binding. A plain class with no
// React: App.tsx creates one instance and `useCloudSync` bridges it to the UI.
// Every operation looks its provider up from the binding, so draws bound to
// different providers live side by side.
//
// Dirty state is not kept here. It is `DocMeta.sceneKey` versus
// `CloudBinding.lastSyncedSceneKey` in IndexedDB, which is what makes offline
// and interrupted edits survive a reload without a queue of their own.

import { packEnvelope, unpackEnvelope } from "../envelope";
import {
  ConflictError,
  NetworkError,
  NotFoundError,
  TokenExpiredError,
} from "../errors";

import type {
  CloudBinding,
  CloudSyncError,
  Doc,
  DocMeta,
} from "../../storage/types";
import type {
  AuthState,
  CloudFileMeta,
  CloudProviderId,
  CloudStorageProvider,
  DrawolaEnvelope,
} from "../types";

export type SyncStatus =
  | { kind: "local" }
  | { kind: "synced" }
  | { kind: "pending" }
  | { kind: "error"; reason: "auth" | CloudSyncError };

export type SyncStoreScene = {
  elements?: Doc["elements"];
  appState?: Doc["appState"];
  files?: Doc["files"];
};

export type SyncStoreUpdate = SyncStoreScene & {
  name?: string;
  cloud?: CloudBinding | null;
};

/** The slice of `multiFileStore` the engine needs. Narrow so tests can fake it. */
export type SyncStore = {
  getDoc(id: string): Promise<Doc | null>;
  getDocMeta(id: string): Promise<DocMeta | null>;
  listDocs(): Promise<DocMeta[]>;
  saveDoc(id: string, update: SyncStoreUpdate): Promise<DocMeta>;
  createDoc(name: string, initial?: SyncStoreScene): Promise<DocMeta>;
};

export type SyncTiming = {
  /** Idle time after the last content write before a push. */
  idleMs: number;
  /** A doc edited without pause is pushed anyway after this long. */
  maxWaitMs: number;
  /** Retry delays after a failed push. The list is also the retry ceiling. */
  backoffMs: number[];
  /** How long a pull waits for the remote metadata before it gives up. */
  statTimeoutMs: number;
};

export type SyncEngineDeps = {
  providers: Partial<Record<CloudProviderId, CloudStorageProvider>>;
  store: SyncStore;
  /** Bumps the lists that render sync state (Studio, breadcrumb). */
  onDocsChanged: () => void;
  /** Writes the editor's pending debounced save, so a pull compares against
   *  the real local state. */
  flushLocalSave: () => Promise<void>;
  /** The store copy of `docId` was replaced by the cloud copy. If it is the
   *  open draw, put it on the canvas. */
  reloadDoc: (docId: string) => Promise<void>;
  /** Content write events from the store. */
  subscribeDocWritten: (listener: (docId: string) => void) => () => void;
  isOnline?: () => boolean;
  now?: () => number;
  timing?: Partial<SyncTiming>;
};

export type SaveNewResult =
  | { ok: true; meta: CloudFileMeta }
  | { ok: false; reason: "unavailable" | "tooLarge" | "failed" };

const DEFAULT_TIMING: SyncTiming = {
  idleMs: 5000,
  maxWaitMs: 60000,
  backoffMs: [30000, 60000, 120000, 300000],
  statTimeoutMs: 8000,
};

type DirtyTimer = {
  timer: ReturnType<typeof setTimeout>;
  firstDirtyAt: number;
};

/** Byte size of an envelope as it will be uploaded. Counted in UTF-8 bytes,
 *  because a CJK character is one JavaScript character but three bytes. */
function envelopeBytes(envelope: DrawolaEnvelope): number {
  const json = JSON.stringify(envelope);
  if (typeof TextEncoder === "undefined") {
    return json.length;
  }
  return new TextEncoder().encode(json).length;
}

/**
 * Keeps bound draws in step with their cloud files: push after local edits,
 * pull on open, and the repair actions behind the exclamation icon.
 */
export class CloudSyncEngine {
  private readonly deps: SyncEngineDeps;
  private readonly timing: SyncTiming;
  private readonly authStates = new Map<CloudProviderId, AuthState>();
  private readonly dirtyTimers = new Map<string, DirtyTimer>();
  private readonly retryTimers = new Map<
    string,
    ReturnType<typeof setTimeout>
  >();
  private readonly attempts = new Map<string, number>();
  private readonly pushing = new Set<string>();
  private readonly listeners = new Set<() => void>();
  private readonly disposers: Array<() => void> = [];
  /** Bumped by `dispose()`. A boot that started before it stops registering. */
  private generation = 0;
  /** Pushes run one after another, never in parallel against the provider. */
  private chain: Promise<void> = Promise.resolve();

  /** Takes its whole world through `deps`. Nothing runs until `boot()`. */
  constructor(deps: SyncEngineDeps) {
    this.deps = deps;
    this.timing = { ...DEFAULT_TIMING, ...deps.timing };
  }

  // --- status ---------------------------------------------------------------

  /** What the icon shows for one draw. Computed, never stored. */
  statusOf(meta: DocMeta): SyncStatus {
    const binding = meta.cloud;
    if (!binding) {
      return { kind: "local" };
    }
    const dirty = this.isDirty(meta);
    if (dirty && !this.isSignedIn(binding.providerId)) {
      return { kind: "error", reason: "auth" };
    }
    if (binding.syncError) {
      return { kind: "error", reason: binding.syncError };
    }
    if (dirty || this.pushing.has(meta.id)) {
      return { kind: "pending" };
    }
    return { kind: "synced" };
  }

  /** Whether the local content moved past what the cloud copy holds. */
  private isDirty(meta: DocMeta): boolean {
    return !!meta.cloud && meta.sceneKey !== meta.cloud.lastSyncedSceneKey;
  }

  /** Whether this provider currently holds a usable session. */
  isSignedIn(providerId: CloudProviderId): boolean {
    return this.authStates.get(providerId)?.status === "signed-in";
  }

  /** Live auth state of one provider, for the sign-in UI. */
  getAuthState(providerId: CloudProviderId): AuthState {
    return this.authStates.get(providerId) ?? { status: "signed-out" };
  }

  /** The adapter this build ships for `providerId`, if any. */
  getProvider(providerId: CloudProviderId): CloudStorageProvider | null {
    return this.deps.providers[providerId] ?? null;
  }

  /** Display name for strings; falls back to the id for a provider this build does not ship. */
  providerName(providerId: CloudProviderId): string {
    return this.deps.providers[providerId]?.displayName ?? providerId;
  }

  /** First provider this build ships, so strings can name a default target
   *  without hardcoding a provider id. `null` when cloud storage is off. */
  firstProviderId(): CloudProviderId | null {
    return this.providerEntries()[0]?.[0] ?? null;
  }

  /** Fires on anything that can change a status: auth, push start and end, binding writes. */
  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /** Tell every subscriber that a status may have moved. */
  private emit(): void {
    for (const listener of this.listeners) {
      listener();
    }
  }

  /** Network reachability, overridable so tests can go offline. */
  private isOnline(): boolean {
    if (this.deps.isOnline) {
      return this.deps.isOnline();
    }
    return typeof navigator === "undefined" || navigator.onLine !== false;
  }

  /** Clock, overridable so tests can pin timestamps. */
  private now(): number {
    return this.deps.now ? this.deps.now() : Date.now();
  }

  // --- scheduling -----------------------------------------------------------

  /** A content write landed; push after the idle delay, or at maxWait. */
  markDirty(docId: string): void {
    // New content is a fresh start, so a doc that ran out of retries earlier
    // gets the whole backoff list again.
    this.attempts.delete(docId);
    const now = this.now();
    const existing = this.dirtyTimers.get(docId);
    if (existing) {
      clearTimeout(existing.timer);
    }
    const firstDirtyAt = existing?.firstDirtyAt ?? now;
    const delay = Math.max(
      0,
      Math.min(
        this.timing.idleMs,
        this.timing.maxWaitMs - (now - firstDirtyAt),
      ),
    );
    const timer = setTimeout(() => {
      this.dirtyTimers.delete(docId);
      void this.push(docId);
    }, delay);
    this.dirtyTimers.set(docId, { timer, firstDirtyAt });
  }

  /** Push everything that is waiting on a timer right now (tab going hidden). */
  flushTimers(): void {
    for (const [docId, entry] of this.dirtyTimers) {
      clearTimeout(entry.timer);
      this.dirtyTimers.delete(docId);
      void this.push(docId);
    }
  }

  /** Queue another push attempt after a transient failure. */
  private scheduleRetry(docId: string): void {
    const attempt = this.attempts.get(docId) ?? 0;
    const delays = this.timing.backoffMs;
    if (attempt >= delays.length) {
      // Out of attempts. The doc stays dirty, so the next boot, `online`
      // event, sign-in or content write starts a fresh cycle instead of
      // uploading the same failing draw forever.
      return;
    }
    const delay = delays[attempt];
    this.attempts.set(docId, attempt + 1);
    const existing = this.retryTimers.get(docId);
    if (existing) {
      clearTimeout(existing);
    }
    this.retryTimers.set(
      docId,
      setTimeout(() => {
        this.retryTimers.delete(docId);
        void this.push(docId);
      }, delay),
    );
  }

  // --- push -----------------------------------------------------------------

  /**
   * Push `docId` now if it is dirty. Queued behind other pushes. Never
   * rejects: timers and event handlers fire this without awaiting it, so an
   * escaping failure (a store write refused because the draw was deleted
   * mid-upload, say) would land as an unhandled rejection.
   */
  push(docId: string): Promise<void> {
    const run = this.chain.then(async () => {
      try {
        await this.withLock(docId, () => this.pushOne(docId));
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("[drawOla] cloud sync push failed", e);
      }
    });
    this.chain = run;
    return run;
  }

  /** Cross-tab exclusion; a tab without the lock skips, the other tab pushes.
   *  Answers `undefined` when the lock was not granted. */
  private async withLock<T>(
    docId: string,
    fn: () => Promise<T>,
  ): Promise<T | undefined> {
    const locks =
      typeof navigator !== "undefined" ? navigator.locks : undefined;
    if (!locks) {
      return await fn();
    }
    let result: T | undefined;
    await locks.request(
      `drawola-sync:${docId}`,
      { ifAvailable: true },
      async (lock) => {
        if (lock) {
          result = await fn();
        }
      },
    );
    return result;
  }

  /**
   * Queue `body` behind the provider work already running and hold the doc's
   * lock while it runs, the way `push` does. `whenSkipped` is the answer for a
   * tab that does not hold the lock. A rejection settles the caller's promise
   * only, never the chain, so one failure cannot stop every later operation.
   */
  private enqueue<T>(
    docId: string,
    body: () => Promise<T>,
    whenSkipped: T,
  ): Promise<T> {
    const run = this.chain.then(() => this.withLock(docId, body));
    this.chain = run.then(
      () => undefined,
      () => undefined,
    );
    return run.then((result) => (result === undefined ? whenSkipped : result));
  }

  /** One push attempt: guards, upload, binding write, error mapping. */
  private async pushOne(docId: string): Promise<void> {
    // Every guard that the metadata can answer runs first, because `getDoc`
    // walks the binaries store and most content writes are on unbound draws.
    const meta = await this.deps.store.getDocMeta(docId);
    if (
      !meta ||
      !meta.cloud ||
      meta.cloud.syncError ||
      !this.isDirty(meta) ||
      !this.deps.providers[meta.cloud.providerId] ||
      !this.isSignedIn(meta.cloud.providerId) ||
      !this.isOnline()
    ) {
      return;
    }
    const doc = await this.deps.store.getDoc(docId);
    const binding = doc?.cloud;
    if (!doc || !binding || binding.syncError || !this.isDirty(doc)) {
      return;
    }
    const provider = this.deps.providers[binding.providerId];
    if (!provider || !this.isSignedIn(binding.providerId) || !this.isOnline()) {
      return;
    }
    const envelope = this.envelopeOf(doc);
    if (this.tooLarge(provider, envelope)) {
      await this.writeBinding(docId, { ...binding, syncError: "tooLarge" });
      return;
    }
    this.pushing.add(docId);
    this.emit();
    try {
      const remote = await provider.writeFile({
        providerFileId: binding.providerFileId,
        name: doc.name,
        envelope,
        ifMatchVersion: binding.lastSyncedVersion,
      });
      this.attempts.delete(docId);
      await this.writeBinding(
        docId,
        this.syncedBinding(binding.providerId, remote, doc.sceneKey),
      );
    } catch (e) {
      await this.handlePushError(docId, binding, e);
    } finally {
      this.pushing.delete(docId);
      this.emit();
    }
  }

  /** The upload payload for one doc. */
  private envelopeOf(doc: Doc): DrawolaEnvelope {
    return packEnvelope({
      name: doc.name,
      elements: doc.elements,
      appState: doc.appState,
      files: doc.files,
      localDocId: doc.id,
    });
  }

  /** Whether this envelope is over what the provider takes in one upload. */
  private tooLarge(
    provider: CloudStorageProvider,
    envelope: DrawolaEnvelope,
  ): boolean {
    return (
      provider.maxUploadBytes !== undefined &&
      envelopeBytes(envelope) > provider.maxUploadBytes
    );
  }

  /** A binding that says local and cloud now hold the same content. */
  private syncedBinding(
    providerId: CloudProviderId,
    remote: CloudFileMeta,
    sceneKey: string | undefined,
  ): CloudBinding {
    return {
      providerId,
      providerFileId: remote.providerFileId,
      lastSyncedVersion: remote.version,
      lastSyncedAt: this.now(),
      lastSyncedSceneKey: sceneKey,
    };
  }

  /** Store a binding (or drop it) and tell the UI. */
  private async writeBinding(
    docId: string,
    binding: CloudBinding | null,
  ): Promise<void> {
    await this.deps.store.saveDoc(docId, { cloud: binding });
    this.deps.onDocsChanged();
    this.emit();
  }

  /** Turn a failed upload into a stored error, a sign-out, or a retry. */
  private async handlePushError(
    docId: string,
    binding: CloudBinding,
    e: unknown,
  ): Promise<void> {
    if (e instanceof ConflictError) {
      await this.writeBinding(docId, { ...binding, syncError: "conflict" });
      return;
    }
    if (e instanceof NotFoundError) {
      await this.writeBinding(docId, { ...binding, syncError: "fileMissing" });
      return;
    }
    if (e instanceof TokenExpiredError) {
      this.authStates.set(binding.providerId, { status: "signed-out" });
      this.emit();
      return;
    }
    if (!(e instanceof NetworkError)) {
      // eslint-disable-next-line no-console
      console.error("[drawOla] cloud sync provider error", e);
    }
    this.scheduleRetry(docId);
  }

  // --- pull -----------------------------------------------------------------

  /**
   * On open: if the cloud copy moved and the local copy is clean, take the
   * cloud copy. Both moved means conflict. Signed out or offline is not an
   * error here; a later push is still guarded by `ifMatchVersion`.
   */
  async pullIfRemoteNewer(docId: string): Promise<void> {
    await this.deps.flushLocalSave();
    // On the same chain and the same lock as a push, so a pull that starts
    // while this doc is being uploaded reads the binding the upload wrote
    // instead of the stale one it started from.
    const run = this.enqueue<void>(docId, () => this.pullOne(docId), undefined);
    // The caller is the open path, so it may not wait behind an upload of some
    // other doc that hangs at the network layer. After `statTimeoutMs` the open
    // goes ahead and the queued pull still runs later in the chain: `reloadDoc`
    // puts a newer remote copy on the canvas when this is the open draw, and
    // `loadRemote` re-checks the sceneKey, so an edit made meanwhile becomes a
    // conflict instead of being overwritten.
    let timer: ReturnType<typeof setTimeout> | undefined;
    const expiry = new Promise<void>((resolve) => {
      timer = setTimeout(resolve, this.timing.statTimeoutMs);
    });
    try {
      await Promise.race([run, expiry]);
    } finally {
      clearTimeout(timer);
    }
  }

  /** The pull body, always inside the chain and the per-doc lock. */
  private async pullOne(docId: string): Promise<void> {
    const meta = await this.deps.store.getDocMeta(docId);
    const binding = meta?.cloud;
    if (!meta || !binding || binding.syncError) {
      return;
    }
    const provider = this.deps.providers[binding.providerId];
    if (!provider || !this.isSignedIn(binding.providerId) || !this.isOnline()) {
      return;
    }
    let remote: CloudFileMeta | null;
    try {
      remote = await this.statWithTimeout(provider, binding.providerFileId);
    } catch (e) {
      await this.handleReadError(docId, binding, e);
      return;
    }
    if (!remote) {
      // The request is stuck. Opening a draw must not wait on it; a later
      // push is still guarded by `ifMatchVersion`.
      return;
    }
    if (remote.version === binding.lastSyncedVersion) {
      return;
    }
    if (this.isDirty(meta)) {
      await this.writeBinding(docId, { ...binding, syncError: "conflict" });
      return;
    }
    try {
      await this.loadRemote(docId, binding, provider);
    } catch (e) {
      await this.handleReadError(docId, binding, e);
    }
  }

  /** Remote metadata, or `null` when the provider takes too long to answer. */
  private async statWithTimeout(
    provider: CloudStorageProvider,
    providerFileId: string,
  ): Promise<CloudFileMeta | null> {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const expiry = new Promise<null>((resolve) => {
      timer = setTimeout(() => resolve(null), this.timing.statTimeoutMs);
    });
    try {
      return await Promise.race([provider.statFile(providerFileId), expiry]);
    } finally {
      clearTimeout(timer);
    }
  }

  /**
   * Replace the local copy with the cloud file. Re-checks the sceneKey after
   * the download: an edit made meanwhile turns this into a conflict instead
   * of throwing the new strokes away.
   */
  private async loadRemote(
    docId: string,
    binding: CloudBinding,
    provider: CloudStorageProvider,
  ): Promise<void> {
    const keyBefore = (await this.deps.store.getDocMeta(docId))?.sceneKey;
    const { meta: remote, envelope } = await provider.readFile(
      binding.providerFileId,
    );
    const { name, scene } = unpackEnvelope(envelope);
    const current = await this.deps.store.getDocMeta(docId);
    if (!current) {
      return;
    }
    if (current.sceneKey !== keyBefore) {
      await this.writeBinding(docId, { ...binding, syncError: "conflict" });
      return;
    }
    const saved = await this.deps.store.saveDoc(docId, {
      elements: scene.elements,
      appState: scene.appState,
      files: scene.files,
      name,
    });
    await this.writeBinding(
      docId,
      this.syncedBinding(binding.providerId, remote, saved.sceneKey),
    );
    await this.deps.reloadDoc(docId);
  }

  /** Turn a failed download into a stored error or a sign-out. */
  private async handleReadError(
    docId: string,
    binding: CloudBinding,
    e: unknown,
  ): Promise<void> {
    if (e instanceof NotFoundError) {
      await this.writeBinding(docId, { ...binding, syncError: "fileMissing" });
      return;
    }
    if (e instanceof TokenExpiredError) {
      this.authStates.set(binding.providerId, { status: "signed-out" });
      this.emit();
      return;
    }
    if (!(e instanceof NetworkError)) {
      // eslint-disable-next-line no-console
      console.error("[drawOla] cloud sync read failed", e);
    }
  }

  // --- repair actions (the panel behind the exclamation icon) ---------------

  /** Interactive sign-in. The click that called this is the popup's user gesture. */
  async signIn(providerId: CloudProviderId): Promise<void> {
    const provider = this.deps.providers[providerId];
    if (!provider) {
      return;
    }
    try {
      await provider.authenticate({ interactive: true });
    } catch {
      // the provider emitted its own error state
    }
    this.authStates.set(providerId, provider.getAuthState());
    this.emit();
  }

  /** Conflict: keep mine (write without ifMatchVersion) or take theirs. */
  async resolveConflict(
    docId: string,
    choice: "overwrite" | "loadRemote",
  ): Promise<void> {
    await this.enqueue<void>(
      docId,
      () => this.resolveConflictOne(docId, choice),
      undefined,
    );
  }

  /** The repair body, always inside the chain and the per-doc lock. */
  private async resolveConflictOne(
    docId: string,
    choice: "overwrite" | "loadRemote",
  ): Promise<void> {
    const meta = await this.deps.store.getDocMeta(docId);
    const binding = meta?.cloud;
    if (!meta || !binding) {
      return;
    }
    const provider = this.deps.providers[binding.providerId];
    if (!provider || !this.isSignedIn(binding.providerId) || !this.isOnline()) {
      return;
    }
    await this.deps.flushLocalSave();
    const clean: CloudBinding = { ...binding, syncError: undefined };
    if (choice === "loadRemote") {
      try {
        await this.loadRemote(docId, clean, provider);
      } catch (e) {
        await this.handleReadError(docId, binding, e);
      }
      return;
    }
    const doc = await this.deps.store.getDoc(docId);
    if (!doc) {
      return;
    }
    await this.writeRemote(doc, clean, provider, {
      providerFileId: binding.providerFileId,
    });
  }

  /** fileMissing: create a new cloud file from the local copy and bind to it. */
  async reupload(docId: string): Promise<void> {
    await this.enqueue<void>(docId, () => this.reuploadOne(docId), undefined);
  }

  /** The reupload body, always inside the chain and the per-doc lock. */
  private async reuploadOne(docId: string): Promise<void> {
    const doc = await this.deps.store.getDoc(docId);
    const binding = doc?.cloud;
    if (!doc || !binding) {
      return;
    }
    const provider = this.deps.providers[binding.providerId];
    if (!provider || !this.isSignedIn(binding.providerId) || !this.isOnline()) {
      return;
    }
    await this.deps.flushLocalSave();
    const fresh = await this.deps.store.getDoc(docId);
    if (!fresh) {
      return;
    }
    await this.writeRemote(fresh, binding, provider, {});
  }

  /** Forget the cloud file. The local draw stays, the remote file is not touched. */
  async unlink(docId: string): Promise<void> {
    await this.writeBinding(docId, null);
  }

  /**
   * Upload `doc` as the whole file (create when `providerFileId` is unset,
   * plain overwrite otherwise) and record the result on the binding.
   */
  private async writeRemote(
    doc: Doc,
    binding: CloudBinding,
    provider: CloudStorageProvider,
    target: { providerFileId?: string },
  ): Promise<void> {
    const envelope = this.envelopeOf(doc);
    if (this.tooLarge(provider, envelope)) {
      await this.writeBinding(doc.id, { ...binding, syncError: "tooLarge" });
      return;
    }
    this.pushing.add(doc.id);
    this.emit();
    try {
      const remote = await provider.writeFile({
        providerFileId: target.providerFileId,
        name: doc.name,
        envelope,
      });
      this.attempts.delete(doc.id);
      await this.writeBinding(
        doc.id,
        this.syncedBinding(binding.providerId, remote, doc.sceneKey),
      );
    } catch (e) {
      await this.handlePushError(doc.id, binding, e);
    } finally {
      this.pushing.delete(doc.id);
      this.emit();
    }
  }

  // --- menu actions ---------------------------------------------------------

  /** "Save as" to a provider: rename the local draw to `name`, create the cloud file, bind. */
  async saveNew(
    docId: string,
    providerId: CloudProviderId,
    name: string,
  ): Promise<SaveNewResult> {
    return this.enqueue<SaveNewResult>(
      docId,
      () => this.saveNewOne(docId, providerId, name),
      { ok: false, reason: "failed" },
    );
  }

  /** The "save as" body, always inside the chain and the per-doc lock. */
  private async saveNewOne(
    docId: string,
    providerId: CloudProviderId,
    name: string,
  ): Promise<SaveNewResult> {
    const provider = this.deps.providers[providerId];
    if (!provider || !this.isSignedIn(providerId) || !this.isOnline()) {
      return { ok: false, reason: "unavailable" };
    }
    await this.deps.flushLocalSave();
    const before = await this.deps.store.getDoc(docId);
    if (!before) {
      return { ok: false, reason: "failed" };
    }
    if (before.name !== name) {
      await this.deps.store.saveDoc(docId, { name });
    }
    const doc = await this.deps.store.getDoc(docId);
    if (!doc) {
      return { ok: false, reason: "failed" };
    }
    const envelope = this.envelopeOf(doc);
    if (this.tooLarge(provider, envelope)) {
      return { ok: false, reason: "tooLarge" };
    }
    this.pushing.add(docId);
    this.emit();
    try {
      const remote = await provider.writeFile({ name: doc.name, envelope });
      await this.writeBinding(
        docId,
        this.syncedBinding(providerId, remote, doc.sceneKey),
      );
      return { ok: true, meta: remote };
    } catch (e) {
      if (e instanceof TokenExpiredError) {
        this.authStates.set(providerId, { status: "signed-out" });
      } else {
        // eslint-disable-next-line no-console
        console.error("[drawOla] cloud save failed", e);
      }
      return { ok: false, reason: "failed" };
    } finally {
      this.pushing.delete(docId);
      this.emit();
    }
  }

  /**
   * Open a cloud file: the local draw already bound to it when there is one,
   * otherwise a new local draw filled from the download. Returns the local
   * doc id. Provider errors propagate to the caller.
   */
  async openRemote(
    providerId: CloudProviderId,
    fileId: string,
  ): Promise<string | null> {
    const provider = this.deps.providers[providerId];
    if (!provider || !this.isSignedIn(providerId) || !this.isOnline()) {
      return null;
    }
    const existing = (await this.deps.store.listDocs()).find(
      (meta) =>
        meta.cloud?.providerId === providerId &&
        meta.cloud.providerFileId === fileId,
    );
    if (existing) {
      return existing.id;
    }
    const { meta: remote, envelope } = await provider.readFile(fileId);
    const { name, scene } = unpackEnvelope(envelope);
    const created = await this.deps.store.createDoc(name, {
      elements: scene.elements,
      appState: scene.appState,
      files: scene.files,
    });
    await this.writeBinding(
      created.id,
      this.syncedBinding(providerId, remote, created.sceneKey),
    );
    return created.id;
  }

  /** Whether the delete dialog may offer to remove the cloud copy right now. */
  canDeleteRemote(meta: DocMeta): boolean {
    const binding = meta.cloud;
    return (
      !!binding &&
      !!this.deps.providers[binding.providerId] &&
      this.isSignedIn(binding.providerId) &&
      this.isOnline()
    );
  }

  /** Delete the cloud file. `true` when it is gone (or was already gone). */
  async deleteRemote(docId: string): Promise<boolean> {
    return this.enqueue<boolean>(
      docId,
      () => this.deleteRemoteOne(docId),
      false,
    );
  }

  /** The delete body, always inside the chain and the per-doc lock. */
  private async deleteRemoteOne(docId: string): Promise<boolean> {
    const meta = await this.deps.store.getDocMeta(docId);
    const binding = meta?.cloud;
    if (!meta || !binding) {
      return true;
    }
    if (!this.canDeleteRemote(meta)) {
      return false;
    }
    const provider = this.deps.providers[binding.providerId]!;
    try {
      await provider.deleteFile(binding.providerFileId);
      return true;
    } catch (e) {
      if (e instanceof NotFoundError) {
        return true;
      }
      if (e instanceof TokenExpiredError) {
        this.authStates.set(binding.providerId, { status: "signed-out" });
        this.emit();
      } else if (!(e instanceof NetworkError)) {
        // eslint-disable-next-line no-console
        console.error("[drawOla] cloud delete failed", e);
      }
      return false;
    }
  }

  // --- lifecycle ------------------------------------------------------------

  /**
   * Init providers, subscribe to auth and store events, start a silent
   * sign-in for every provider that has bound draws, then push what is dirty.
   */
  async boot(): Promise<void> {
    const generation = this.generation;
    for (const [id, provider] of this.providerEntries()) {
      this.register(
        generation,
        provider.subscribeAuth((state) => {
          this.authStates.set(id, state);
          this.emit();
          if (state.status === "signed-in") {
            void this.retryProvider(id);
          }
        }),
      );
      try {
        await provider.init();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("[drawOla] cloud provider init failed", id, e);
      }
      if (generation !== this.generation) {
        return;
      }
      this.authStates.set(id, provider.getAuthState());
    }
    this.register(
      generation,
      this.deps.subscribeDocWritten((docId) => this.markDirty(docId)),
    );
    if (typeof window !== "undefined") {
      const onOnline = () => void this.retryAll();
      const onVisibility = () => {
        if (document.visibilityState === "hidden") {
          this.flushTimers();
        }
      };
      window.addEventListener("online", onOnline);
      document.addEventListener("visibilitychange", onVisibility);
      this.register(generation, () => {
        window.removeEventListener("online", onOnline);
        document.removeEventListener("visibilitychange", onVisibility);
      });
    }
    const metas = await this.deps.store.listDocs();
    if (generation !== this.generation) {
      return;
    }
    const bound = new Set<CloudProviderId>();
    for (const meta of metas) {
      if (meta.cloud) {
        bound.add(meta.cloud.providerId);
      }
    }
    for (const id of bound) {
      this.startSilentSignIn(id, generation);
    }
    this.emit();
    await this.retryAll(metas);
  }

  /**
   * Ask a provider for a session without a click. Never awaited: a provider
   * whose popup is blocked can leave this pending for the whole session, and
   * the boot scan must not wait on that. The auth listener picks the result
   * up and pushes what is dirty.
   */
  private startSilentSignIn(
    providerId: CloudProviderId,
    generation: number,
  ): void {
    const provider = this.deps.providers[providerId];
    if (!provider || this.isSignedIn(providerId)) {
      return;
    }
    void provider
      .authenticate({ interactive: false })
      .catch(() => {
        // No session or a popup blocker. The icon plus a click is the way back.
      })
      .then(() => {
        if (generation !== this.generation) {
          return;
        }
        this.authStates.set(providerId, provider.getAuthState());
        this.emit();
      });
  }

  /**
   * Keep a teardown callback for `dispose()`. A `dispose()` that landed while
   * this boot was awaiting runs it at once instead, so the listener a
   * double-invoked effect registered late cannot outlive its engine.
   */
  private register(generation: number, dispose: () => void): void {
    if (generation !== this.generation) {
      dispose();
      return;
    }
    this.disposers.push(dispose);
  }

  /** Push every dirty bound draw that is not waiting on the user. */
  async retryAll(metas?: DocMeta[]): Promise<void> {
    const all = metas ?? (await this.deps.store.listDocs());
    for (const meta of all) {
      if (meta.cloud && !meta.cloud.syncError && this.isDirty(meta)) {
        // Boot, `online` and a sign-in are all fresh starts, so a doc that
        // ran out of retries earlier gets the whole backoff list again.
        this.attempts.delete(meta.id);
        await this.push(meta.id);
      }
    }
  }

  /** Push the dirty draws bound to one provider, after it signs in. */
  private async retryProvider(providerId: CloudProviderId): Promise<void> {
    const all = await this.deps.store.listDocs();
    await this.retryAll(
      all.filter((meta) => meta.cloud?.providerId === providerId),
    );
  }

  /** The providers this build actually ships, as typed pairs. */
  private providerEntries(): Array<[CloudProviderId, CloudStorageProvider]> {
    return Object.entries(this.deps.providers).filter(
      (entry): entry is [CloudProviderId, CloudStorageProvider] => !!entry[1],
    );
  }

  /** Stop timers and subscriptions. A boot still running registers nothing
   *  more after this. */
  dispose(): void {
    this.generation += 1;
    for (const entry of this.dirtyTimers.values()) {
      clearTimeout(entry.timer);
    }
    this.dirtyTimers.clear();
    for (const timer of this.retryTimers.values()) {
      clearTimeout(timer);
    }
    this.retryTimers.clear();
    for (const dispose of this.disposers.splice(0)) {
      dispose();
    }
  }
}
